import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { AlertTriangle, Link2, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useTask, useUpdateTask, useDeleteTask, useFlagBlocker, useClearBlocker, usePreviewAssign, useLoanTask, useTaskDependencies, useAddDependency, useRemoveDependency, useTaskList } from '../../api/tasks';
import { useEpicsByProject } from '../../api/epics';
import { useSubmitFeedback } from '../../api/feedback';
import { useThresholds } from '../../api/thresholds';
import { useEngineerList } from '../../api/engineers';
import { useProjectList } from '../../api/projects';
import { useSprintList } from '../../api/sprints';
import { applyServerErrors } from '../../lib/formErrors';
import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import { DetailPageSkeleton } from '@/components/ui/skeleton';
import { useConfirm } from '@/components/ui/confirm-dialog';
import ErrorState from '../../components/ui/ErrorState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { formatDate, parseDueDateEndOfDay } from '../../lib/dates';
import type { TaskDto, TaskStatus, ThresholdsDto, PreviewAssignResult, BugSeverity } from '../../types/api';

const SELECT_CLS = 'h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring';

const STATUS_VARIANT: Record<TaskStatus, 'green' | 'red' | 'gray'> = {
  active: 'green', blocked: 'red', done: 'gray',
};

function computeEscalationStatus(
  task: TaskDto, thresholds: ThresholdsDto,
): { label: string; cls: string } | null {
  if (task.status === 'done') return null;
  const now       = Date.now();
  const activated = new Date(task.activatedAt).getTime();
  const due       = parseDueDateEndOfDay(task.dueDate).getTime();
  const totalDays = (due - activated) / 86_400_000;
  const daysUntilDue = (due - now) / 86_400_000;

  if (daysUntilDue <= 0 || totalDays <= 0)
    return { label: 'Overdue', cls: 'text-destructive font-semibold' };

  const elapsedPct  = (now - activated) / (due - activated);
  const t3Threshold = Math.max(thresholds.escalationT3ElapsedPct, 1 - thresholds.escalationT3Days / totalDays);
  const t1Threshold = Math.max(thresholds.escalationT1ElapsedPct, 1 - thresholds.escalationT1Days / totalDays);
  const hoursLeft   = daysUntilDue * 24;
  const days = (n: number) => `${Math.ceil(n)} day${Math.ceil(n) === 1 ? '' : 's'}`;

  if (elapsedPct >= t1Threshold && hoursLeft >= thresholds.escalationT1MinHours)
    return { label: `T-1 reached — ${days(daysUntilDue)} left`, cls: 'text-orange-600 font-semibold' };
  if (elapsedPct >= t3Threshold && hoursLeft >= thresholds.escalationT3MinHours)
    return { label: `T-3 reached — ${days(daysUntilDue)} left`, cls: 'text-yellow-600 font-semibold' };
  const daysUntilT3 = Math.max(0, (activated + t3Threshold * totalDays * 86_400_000 - now) / 86_400_000);
  if (daysUntilT3 < 1) return { label: 'T-3 in < 1 day', cls: 'text-yellow-600' };
  return { label: `On track — T-3 in ~${Math.round(daysUntilT3)} days`, cls: 'text-muted-foreground' };
}

function SignalRow({ label, tripped, reason }: { label: string; tripped: boolean; reason: string }) {
  return (
    <div className="flex items-start gap-2 text-xs">
      <span className={cn('mt-0.5 h-2 w-2 shrink-0 rounded-full', tripped ? 'bg-destructive' : 'bg-emerald-400')} />
      <span className="w-24 shrink-0 font-medium text-foreground">{label}</span>
      <span className="text-muted-foreground">{reason}</span>
    </div>
  );
}

function OverworkPreviewCard({ preview, onConfirm, onCancel, loading }: {
  preview: PreviewAssignResult; onConfirm: () => void; onCancel: () => void; loading: boolean;
}) {
  return (
    <Card className="mb-5 border-yellow-300 bg-yellow-50">
      <CardContent className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <h3 className="text-sm font-semibold text-yellow-800">Overwork warning</h3>
        </div>
        <p className="mb-4 text-xs text-yellow-700">Reassigning this task would flag an overwork concern for the new assignee.</p>
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {(['before', 'after'] as const).map((phase) => (
            <div key={phase}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{phase}</p>
              <div className="space-y-1.5">
                <SignalRow label="Load"       tripped={preview[phase].loadVsBaseline.tripped}  reason={preview[phase].loadVsBaseline.reason} />
                <SignalRow label="Concurrent" tripped={preview[phase].concurrent.tripped}       reason={preview[phase].concurrent.reason} />
                <SignalRow label="Stale"      tripped={preview[phase].staleInProgress.tripped}  reason={preview[phase].staleInProgress.reason} />
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={onConfirm} loading={loading}>Confirm anyway</Button>
          <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
        </div>
      </CardContent>
    </Card>
  );
}

const SEVERITY_COLORS: Record<BugSeverity, string> = {
  Low:      'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
  Medium:   'bg-yellow-50 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-400',
  High:     'bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400',
  Critical: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400',
};

interface EditForm    { title: string; description: string; acceptanceCriteria: string; severity: string; points: number; dueDate: string; assigneeId: string; type: string; sprintId: string; epicId: string; }
interface BlockerForm { reason: string; }
interface FeedbackForm { text: string; }
interface LoanForm    { targetEngineerId: string; reason: string; }

export default function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser, can } = useAuth();

  const { data: task, isLoading, error, refetch } = useTask(id!);
  const { data: engineers }    = useEngineerList();
  const { data: projects }     = useProjectList();
  const { data: thresholds }   = useThresholds();
  const { data: sprints }      = useSprintList();
  const { data: links }        = useTaskDependencies(id!);
  const { data: epics }        = useEpicsByProject(task?.projectId ?? '');
  const { data: allTasks }     = useTaskList({ projectId: task?.projectId, limit: 200 });
  const updateTask    = useUpdateTask(id!);
  const deleteTask    = useDeleteTask();
  const flagBlocker   = useFlagBlocker(id!);
  const clearBlocker  = useClearBlocker(id!);
  const previewAssign = usePreviewAssign(id!);
  const submitFeedback = useSubmitFeedback();
  const loanTask      = useLoanTask(id!);
  const addDep        = useAddDependency(id!);
  const removeDep     = useRemoveDependency(id!);

  const [showEdit,     setShowEdit]     = useState(false);
  const [showBlocker,  setShowBlocker]  = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showLoan,     setShowLoan]     = useState(false);
  const [pendingEdit,  setPendingEdit]  = useState<EditForm | null>(null);
  const [preview,      setPreview]      = useState<PreviewAssignResult | null>(null);
  const [depTaskId,    setDepTaskId]    = useState('');

  const editForm     = useForm<EditForm>();
  const blockerForm  = useForm<BlockerForm>();
  const feedbackForm = useForm<FeedbackForm>();
  const loanForm     = useForm<LoanForm>();
  const { confirm, dialog } = useConfirm();

  if (isLoading)        return <DetailPageSkeleton />;
  if (error || !task)   return <ErrorState onRetry={refetch} />;

  const isOwner = task.assigneeId === currentUser!.id;
  const canEdit = can('project_manager');
  const canLoan = can('team_lead');
  const canAct  = isOwner || canEdit;

  const proj = projects?.find((p) => p.id === task.projectId);
  const eng  = engineers?.find((e) => e.id === task.assigneeId);

  const doUpdate = (values: EditForm) => {
    const removeFromSprint = !values.sprintId && !!task.sprintId;
    const removeFromEpic = !values.epicId && !!task.epicId;
    updateTask.mutate(
      { ...values, acceptanceCriteria: values.acceptanceCriteria || undefined, severity: values.severity || undefined, epicId: values.epicId || undefined, removeFromEpic: removeFromEpic || undefined, points: Number(values.points), type: values.type || undefined, sprintId: values.sprintId || undefined, removeFromSprint: removeFromSprint || undefined },
      {
        onSuccess: () => { toast.success('Task updated.'); setShowEdit(false); setPendingEdit(null); setPreview(null); },
        onError:   (e) => applyServerErrors(e, editForm.setError),
      },
    );
  };

  const handleEdit = editForm.handleSubmit((values) => {
    const assigneeChanged = values.assigneeId && values.assigneeId !== (task.assigneeId ?? '');
    if (assigneeChanged) {
      previewAssign.mutate(values.assigneeId, {
        onSuccess: (result) => result.wouldFlagOverwork ? (setPendingEdit(values), setPreview(result)) : doUpdate(values),
        onError:   () => doUpdate(values),
      });
    } else {
      doUpdate(values);
    }
  });

  const handleStatusChange = (status: TaskStatus) =>
    updateTask.mutate({ status }, { onSuccess: () => toast.success('Status updated.') });

  const handleDelete = async () => {
    if (!await confirm({ title: 'Delete this task?', description: 'This cannot be undone.', confirmLabel: 'Delete task' })) return;
    deleteTask.mutate(id!, { onSuccess: () => navigate('/tasks') });
  };

  const handleFlagBlocker = blockerForm.handleSubmit((values) =>
    flagBlocker.mutate(values.reason, {
      onSuccess: () => { toast.success('Blocker flagged.'); setShowBlocker(false); blockerForm.reset(); },
    }),
  );

  const handleClearBlocker = () =>
    clearBlocker.mutate(undefined, { onSuccess: () => toast.success('Blocker cleared.') });

  const handleFeedback = feedbackForm.handleSubmit((values) =>
    submitFeedback.mutate({ text: values.text, taskId: id }, {
      onSuccess: () => { toast.success('Feedback submitted.'); setShowFeedback(false); feedbackForm.reset(); },
    }),
  );

  return (
    <div className="max-w-2xl">
      {dialog}
      <PageHeader
        title={task.title}
        actions={
          canEdit ? (
            <div className="flex gap-2">
              <Button variant="secondary" size="sm"
                onClick={() => {
                  editForm.reset({ title: task.title, description: task.description ?? '', acceptanceCriteria: task.acceptanceCriteria ?? '', severity: task.severity ?? '', points: task.points, dueDate: task.dueDate.slice(0, 10), assigneeId: task.assigneeId ?? '', type: task.taskType, sprintId: task.sprintId ?? '', epicId: task.epicId ?? '' });
                  setPendingEdit(null); setPreview(null); setShowEdit((v) => !v);
                }}
              >
                Edit
              </Button>
              <Button variant="danger" size="sm" onClick={handleDelete} loading={deleteTask.isPending}>Delete</Button>
            </div>
          ) : undefined
        }
      />

      {/* Details card */}
      <Card className={cn(
        'mb-5 overflow-hidden border-l-4',
        task.status === 'blocked' ? 'border-l-red-400'     :
        task.status === 'done'    ? 'border-l-emerald-400' :
                                    'border-l-primary',
      )}>
        <CardContent className="p-5">
          {/* Meta strip */}
          <div className="mb-5 flex flex-wrap items-center gap-2">
            <Badge label={task.status} variant={STATUS_VARIANT[task.status]} />
            <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">{task.taskType}</span>
            <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">{task.points} pts</span>
            {task.severity && (
              <span className={cn('rounded-full px-2.5 py-0.5 text-xs font-medium', SEVERITY_COLORS[task.severity])}>
                {task.severity}
              </span>
            )}
          </div>

          {/* Metadata grid */}
          <div className="grid grid-cols-1 gap-x-6 gap-y-4 text-sm sm:grid-cols-3">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Project</p>
              <p className="mt-1 font-medium text-foreground">{proj?.name ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Assignee</p>
              <p className="mt-1 font-medium text-foreground">{eng?.name ?? 'Unassigned'}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Sprint</p>
              <p className="mt-1 font-medium text-foreground">
                {task.sprintId ? (sprints?.find((s) => s.id === task.sprintId)?.name ?? task.sprintId.slice(0, 8)) : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Due date</p>
              <p className="mt-1 font-medium text-foreground">{formatDate(task.dueDate)}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Activated</p>
              <p className="mt-1 font-medium text-foreground">{formatDate(task.activatedAt)}</p>
            </div>
            {thresholds && task.status !== 'done' && (() => {
              const esc = computeEscalationStatus(task, thresholds);
              return esc ? (
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Escalation</p>
                  <p className={cn('mt-1 text-sm', esc.cls)}>{esc.label}</p>
                </div>
              ) : null;
            })()}
          </div>

          {task.description && (
            <div className="mt-5 border-t border-border pt-4">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Description</p>
              <p className="whitespace-pre-wrap text-sm text-foreground">{task.description}</p>
            </div>
          )}

          {task.acceptanceCriteria && (
            <div className="mt-5 border-t border-border pt-4">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Acceptance criteria</p>
              <p className="whitespace-pre-wrap text-sm text-foreground">{task.acceptanceCriteria}</p>
            </div>
          )}

          {task.blockerReason && (
            <div className="mt-5 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900/40 dark:bg-red-950/20">
              <p className="text-xs font-semibold uppercase tracking-wide text-red-600">Blocker</p>
              <p className="mt-1 text-sm text-red-700 dark:text-red-400">{task.blockerReason}</p>
            </div>
          )}
        </CardContent>

        {((canAct && task.status !== 'done') || (canLoan && task.assigneeId && task.status !== 'done')) && (
          <div className="flex flex-wrap items-center gap-2 border-t border-border bg-muted/30 px-5 py-3">
            {canAct && task.status === 'active' && (
              <>
                <Button variant="secondary" size="sm" onClick={() => handleStatusChange('done')} loading={updateTask.isPending}>Mark done</Button>
                <Button variant="secondary" size="sm" onClick={() => setShowBlocker((v) => !v)}>Flag blocker</Button>
              </>
            )}
            {canAct && task.status === 'blocked' && (
              <Button variant="secondary" size="sm" onClick={handleClearBlocker} loading={clearBlocker.isPending}>Clear blocker</Button>
            )}
            {canLoan && task.assigneeId && (
              <Button variant="secondary" size="sm" onClick={() => { setShowLoan((v) => !v); loanForm.reset(); }}>Loan task</Button>
            )}
          </div>
        )}
      </Card>

      {showBlocker && (
        <Card className="mb-5">
          <CardHeader className="pb-4"><CardTitle className="text-sm">Flag a blocker</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleFlagBlocker} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="blocker-reason">Reason</Label>
                <Textarea id="blocker-reason" rows={3} placeholder="Describe what is blocking progress…"
                  {...blockerForm.register('reason', { required: 'Reason is required' })} />
                {blockerForm.formState.errors.reason && (
                  <p className="text-xs text-destructive">{blockerForm.formState.errors.reason.message}</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button size="sm" type="submit" loading={flagBlocker.isPending}>Submit</Button>
                <Button size="sm" variant="ghost" type="button" onClick={() => setShowBlocker(false)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {showLoan && canLoan && (
        <Card className="mb-5">
          <CardHeader className="pb-4"><CardTitle className="text-sm">Loan task to another team</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={loanForm.handleSubmit((values) =>
              loanTask.mutate({ targetEngineerId: values.targetEngineerId, reason: values.reason || undefined }, {
                onSuccess: () => { toast.success('Task loaned successfully.'); setShowLoan(false); loanForm.reset(); },
                onError:   (e) => applyServerErrors(e, loanForm.setError),
              })
            )} className="space-y-3">
              <div className="space-y-1.5">
                <Label>Target engineer <span className="text-destructive">*</span></Label>
                <select {...loanForm.register('targetEngineerId', { required: 'Select a target engineer' })} className={SELECT_CLS}>
                  <option value="">Select engineer…</option>
                  {engineers?.filter((e) => e.id !== task.assigneeId && e.isActive).map((e) => (
                    <option key={e.id} value={e.id}>{e.name}</option>
                  ))}
                </select>
                {loanForm.formState.errors.targetEngineerId && (
                  <p className="text-xs text-destructive">{loanForm.formState.errors.targetEngineerId.message}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Reason (optional)</Label>
                <Input {...loanForm.register('reason')} placeholder="e.g. Domain expertise needed" />
              </div>
              <div className="flex gap-2 pt-1">
                <Button size="sm" type="submit" loading={loanTask.isPending}>Loan task</Button>
                <Button size="sm" variant="ghost" type="button" onClick={() => setShowLoan(false)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {preview && pendingEdit && (
        <OverworkPreviewCard
          preview={preview}
          onConfirm={() => doUpdate(pendingEdit)}
          onCancel={() => { setPendingEdit(null); setPreview(null); }}
          loading={updateTask.isPending}
        />
      )}

      {showEdit && canEdit && !preview && (
        <Card className="mb-5">
          <CardHeader className="pb-4"><CardTitle className="text-sm">Edit task</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleEdit} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Title</Label>
                <Input {...editForm.register('title', { required: 'Title is required' })} />
                {editForm.formState.errors.title && <p className="text-xs text-destructive">{editForm.formState.errors.title.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label>Description</Label>
                <Textarea rows={3} {...editForm.register('description')} />
              </div>
              <div className="space-y-1.5">
                <Label>Acceptance criteria</Label>
                <Textarea rows={3} placeholder="Define what done looks like…" {...editForm.register('acceptanceCriteria')} />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Points</Label>
                  <Input type="number" min={1} {...editForm.register('points')} />
                </div>
                <div className="space-y-1.5">
                  <Label>Due date</Label>
                  <Input type="date" {...editForm.register('dueDate')} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Type</Label>
                <select {...editForm.register('type')} className={SELECT_CLS}>
                  <option value="Feature">Feature</option>
                  <option value="Bug">Bug</option>
                  <option value="Test">Test</option>
                  <option value="Review">Review</option>
                  <option value="Chore">Chore</option>
                </select>
              </div>
              {editForm.watch('type') === 'Bug' && (
                <div className="space-y-1.5">
                  <Label>Severity</Label>
                  <select {...editForm.register('severity')} className={SELECT_CLS}>
                    <option value="">No severity</option>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Assignee</Label>
                <select {...editForm.register('assigneeId')} className={SELECT_CLS}>
                  <option value="">Unassigned</option>
                  {engineers?.filter((e) => e.isActive).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              {(epics?.length ?? 0) > 0 && (
                <div className="space-y-1.5">
                  <Label>Epic</Label>
                  <select {...editForm.register('epicId')} className={SELECT_CLS}>
                    <option value="">No epic</option>
                    {epics!.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}
                  </select>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Sprint</Label>
                <select {...editForm.register('sprintId')} className={SELECT_CLS}>
                  <option value="">No sprint</option>
                  {sprints?.filter((s) => s.status !== 'Completed' && (!eng?.teamId || s.teamId === eng.teamId)).map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2 pt-1">
                <Button size="sm" type="submit" loading={updateTask.isPending || previewAssign.isPending}>Save</Button>
                <Button size="sm" variant="ghost" type="button" onClick={() => setShowEdit(false)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Dependencies */}
      {((links?.blockedBy.length ?? 0) > 0 || (links?.blocks.length ?? 0) > 0 || canEdit) && (
        <Card className="mt-4 overflow-hidden">
          <div className="flex items-center gap-2.5 border-b border-border px-5 py-3">
            <Link2 className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm font-semibold text-foreground">Dependencies</p>
          </div>
          <div className="px-5 py-4 space-y-4">
            {(links?.blockedBy.length ?? 0) > 0 && (
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Blocked by</p>
                <div className="space-y-1.5">
                  {links!.blockedBy.map((t) => (
                    <div key={t.id} className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
                      <Link to={`/tasks/${t.id}`} className="font-medium text-foreground hover:underline">{t.title}</Link>
                      <div className="flex items-center gap-2">
                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{t.status}</span>
                        {canEdit && (
                          <button onClick={() => removeDep.mutate(t.id)} className="text-muted-foreground hover:text-destructive transition-colors">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(links?.blocks.length ?? 0) > 0 && (
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Blocks</p>
                <div className="space-y-1.5">
                  {links!.blocks.map((t) => (
                    <div key={t.id} className="flex items-center gap-3 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
                      <Link to={`/tasks/${t.id}`} className="flex-1 font-medium text-foreground hover:underline">{t.title}</Link>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{t.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {canEdit && (
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Add blocking task</p>
                <div className="flex items-center gap-2">
                  <select
                    value={depTaskId}
                    onChange={(e) => setDepTaskId(e.target.value)}
                    className="h-8 flex-1 rounded-md border border-input bg-background px-2 text-xs shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                  >
                    <option value="">Select a task that blocks this one…</option>
                    {(allTasks?.items ?? [])
                      .filter((t) => t.id !== id && !links?.blockedBy.some((b) => b.id === t.id))
                      .map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                  </select>
                  <button
                    onClick={() => {
                      if (!depTaskId) return;
                      addDep.mutate(depTaskId, {
                        onSuccess: () => { toast.success('Dependency added.'); setDepTaskId(''); },
                        onError:   (e: unknown) => toast.error((e as Error).message ?? 'Failed to add dependency.'),
                      });
                    }}
                    disabled={!depTaskId || addDep.isPending}
                    className="h-8 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Feedback */}
      <Card className="mt-4">
        <button
          type="button"
          className="flex w-full items-center justify-between px-5 py-3 text-left text-sm font-medium text-foreground hover:bg-muted/40"
          onClick={() => setShowFeedback((v) => !v)}
        >
          <span>Leave feedback on this task</span>
          <span className="text-muted-foreground text-xs">{showFeedback ? '▲' : '▼'}</span>
        </button>
        {showFeedback && (
          <form onSubmit={handleFeedback} className="border-t border-border px-5 pb-5 pt-4">
            <p className="mb-3 rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-700">
              Feedback is readable only by the Head of R&D. This is enforced at the database level.
            </p>
            <div className="mb-3 space-y-1.5">
              <Label htmlFor="feedback-text">Your feedback</Label>
              <Textarea id="feedback-text" rows={4} placeholder="What went well? What could be improved?"
                {...feedbackForm.register('text', { required: 'Feedback text is required' })} />
              {feedbackForm.formState.errors.text && (
                <p className="text-xs text-destructive">{feedbackForm.formState.errors.text.message}</p>
              )}
            </div>
            <div className="flex gap-2">
              <Button size="sm" type="submit" loading={submitFeedback.isPending}>Submit</Button>
              <Button size="sm" variant="ghost" type="button" onClick={() => { setShowFeedback(false); feedbackForm.reset(); }}>Cancel</Button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
